/**
 * TIW Display — contratto dati client ⇄ server.
 *
 * Regole di progetto:
 *  - Il server invia VALORI e CODICI (stato, verdetto), mai testo UI: le frasi
 *    italiane le compone il client (src/i18n/it.ts). Eccezione: i "Lo sapevi?"
 *    che sono contenuti editoriali generati lato server.
 *  - Le soglie e le valutazioni (in linea / da approfondire) le calcola il server,
 *    a partire dalla tabella "Soglie e metodo" del portale. Il monitor non decide nulla.
 *  - Ogni slice porta `updatedAt`: il client mostra "dato non aggiornato" se è vecchio.
 *  - Numeri sempre in unità SI/di dominio (kW, kWh, °C, %, lx, dB, µg/m³), mai stringhe formattate.
 */

export const PROTOCOL_VERSION = 1 as const;

/** Epoch in millisecondi (UTC). Usato ovunque al posto di stringhe ISO per compattezza. */
export type EpochMs = number;
/** Orario locale del sito "HH:MM" (Europe/Rome) per finestre ricorrenti. */
export type LocalTime = string;

export type ScreenId = 'monitor-a' | 'monitor-b' | 'tablet';

/** Esito di una valutazione rispetto al riferimento tecnico. */
export type Status =
  | 'ok' // In linea
  | 'warn' // Da approfondire
  | 'crit' // Critico (es. perdita d'acqua)
  | 'nd'; // Non valutato (sensore assente, dato stimato, luci spente…)

/* ------------------------------------------------------------------ */
/* Domini (slice) dello stato di un display                            */
/* ------------------------------------------------------------------ */

export interface SiteSlice {
  siteId: string;
  clientName: string; // "JANUS"
  addressLine: string; // "Centro Direzionale, Edificio F4 · 15° piano"
  city: string; // "Napoli"
  timezone: string; // "Europe/Rome"
  branding: { accent: string; logoUrl: string | null };
  seatsTotal: number; // 23
  updatedAt: EpochMs;
}

export type Sky = 'clear' | 'partly_cloudy' | 'cloudy' | 'rain' | 'storm' | 'fog' | 'snow';

export interface WeatherSlice {
  tempC: number | null;
  humidityPct: number | null;
  sky: Sky | null;
  isNight: boolean;
  source: string; // es. "open-meteo"
  updatedAt: EpochMs;
}

/** Serie temporale compatta: tuple [epochMs, valore]. */
export type Point = [EpochMs, number];

export interface TimeSeries {
  key: string; // "power_kw"
  unit: string; // "kW"
  bucketSec: number; // passo dei punti in secondi (300 o 900): il grafico usa gli istanti, non il passo
  from: EpochMs; // inizio finestra (es. oggi 06:00)
  to: EpochMs; // fine finestra (es. oggi 22:30)
  points: Point[];
}

export type EnergyPhase = 'idle' | 'opening' | 'working' | 'closing';
export type MeasureMethod = 'measured' | 'derived' | 'estimated';

export interface EnergyBreakdownItem {
  key: 'hvac' | 'coffee' | 'other' | (string & {});
  label: string; // etichetta configurabile dal portale
  energyKwh: number;
  method: MeasureMethod; // "misurato" / "per differenza"
}

export interface EnergySlice {
  powerKw: number; // potenza istantanea
  powerScaleKw: number; // fondo scala dell'anello (es. 6)
  todayKwh: number;
  costTodayEur: number;
  tariffEurKwh: number;
  peakKw: number;
  peakAt: EpochMs | null;
  phase: EnergyPhase; // pilota il titolo "Consumi regolari…"
  breakdown: EnergyBreakdownItem[];
  series: TimeSeries;
  /** Profilo tipico di un giorno feriale (stessa finestra e passo della serie). Opzionale: se manca il grafico non lo disegna. */
  typical?: TimeSeries;
  measuredAt: EpochMs; // timestamp dell'ultima lettura del contatore
  updatedAt: EpochMs;
}

export interface Reference {
  thresholdId: string; // id della riga in "Soglie e metodo" (es. "IND-B02")
  label: string; // "20–24 °C"
  source: string; // "UNI EN 16798-1, cat. II"
  nature:
    | 'norma_volontaria'
    | 'linea_guida'
    | 'linea_guida_internazionale'
    | 'formula_interna'
    | 'governance';
}

/** Controlli del pannello "Stato di salute dell'ufficio" — unione discriminata per `key`. */
export type HealthCheck =
  | (HealthBase & { key: 'air'; values: { pm25: number | null } })
  | (HealthBase & { key: 'water'; values: { leak: boolean; batteryPct: number | null } })
  | (HealthBase & { key: 'hvac'; values: { on: boolean; powerKw: number | null } })
  | (HealthBase & { key: 'electrical'; values: { voltageV: number | null } })
  | (HealthBase & { key: 'sensors'; values: { online: number; total: number } });

interface HealthBase {
  status: Status;
  reference: Reference | null;
  updatedAt: EpochMs;
}

export type HealthCheckKey = HealthCheck['key'];

export interface HealthSlice {
  overall: Status;
  okCount: number;
  total: number;
  checks: HealthCheck[];
  checkedAt: EpochMs;
  updatedAt: EpochMs;
}

export interface Fact {
  id: string;
  text: string; // testo editoriale già pronto
}

export interface FactsSlice {
  items: Fact[];
  rotateSec: number; // 7
  updatedAt: EpochMs;
}

export type ComfortParamKey = 'temperature' | 'humidity' | 'illuminance' | 'noise' | 'pm25' | 'co2';

export interface ComfortParam {
  key: ComfortParamKey;
  value: number | null;
  unit: string; // "°C", "%", "lx", "dB"
  status: Status;
  /** Esito qualitativo: 'low' | 'ok' | 'high' — il client lo traduce ("Un po' fresca", "Perfetta"…). */
  verdict: 'low' | 'ok' | 'high' | 'off';
  scale: { min: number; max: number }; // estremi della barra
  zone: { lo: number; hi: number; oneSided?: 'min' | 'max' }; // fascia verde
  score: number | null; // 0–100, contributo all'indice
  estimated: boolean; // es. CO₂ stimata da VOC, lux a soffitto
  reference: Reference;
  measuredAt: EpochMs;
}

export interface ComfortSlice {
  roomId: string; // "open-space"
  roomName: string;
  index: number | null; // 0–100
  formulaVersion: string; // "TIW v0.1"
  inLineCount: number;
  evaluatedCount: number;
  params: ComfortParam[];
  updatedAt: EpochMs;
}

export interface MoodSlice {
  periodFrom: EpochMs; // inizio periodo (es. oggi 00:00)
  responses: number;
  minResponses: number; // regola privacy: sotto questa soglia non si pubblica
  published: boolean; // false ⇒ score/distribution sono null
  score10: number | null; // 0–10
  /** Conteggi per opzione, in ordine: Alla grande, Bene, Così così, Stanco, Giornata no. */
  distribution: [number, number, number, number, number] | null;
  updatedAt: EpochMs;
}

export interface RoomOccupancy {
  roomId: string;
  name: string;
  capacity: number;
  occupied: boolean;
  /** null se la stanza ha solo un sensore di presenza sì/no (ZT24). */
  count: number | null;
  countSource: 'counter' | 'presence_only' | 'estimate';
  activity: 'empty' | 'working' | 'meeting';
  updatedAt: EpochMs;
}

export interface OccupancySlice {
  totalCount: number | null;
  capacity: number;
  rooms: RoomOccupancy[];
  updatedAt: EpochMs;
}

export interface IndoorSlice {
  roomId: string;
  tempC: number | null;
  humidityPct: number | null;
  updatedAt: EpochMs;
}

export interface CoffeeSlice {
  count: number;
  firstAt: EpochMs | null;
  lastAt: EpochMs | null;
  energyKwh: number;
  busiestWindow: { from: LocalTime; to: LocalTime } | null;
  updatedAt: EpochMs;
}

/** Stato completo che un display può ricevere. Ogni schermo si abbona a un sottoinsieme. */
export interface DisplayState {
  site: SiteSlice;
  weather: WeatherSlice;
  energy: EnergySlice;
  health: HealthSlice;
  facts: FactsSlice;
  comfort: ComfortSlice;
  mood: MoodSlice;
  occupancy: OccupancySlice;
  indoor: IndoorSlice;
  coffee: CoffeeSlice;
}

export type Domain = keyof DisplayState;

/** Domini richiesti da ciascuno schermo (il server filtra di conseguenza). */
export const SCREEN_DOMAINS: Record<ScreenId, Domain[]> = {
  'monitor-a': ['site', 'weather', 'energy', 'health', 'facts'],
  'monitor-b': ['site', 'weather', 'comfort', 'mood', 'occupancy', 'indoor', 'coffee'],
  // Il tablet mostra solo dati già pubblici (umore aggregato, caffè, energia, stanze): il server concede quelli che vuole.
  tablet: ['site', 'indoor', 'coffee', 'mood', 'energy', 'occupancy'],
};

/* ------------------------------------------------------------------ */
/* Messaggi WebSocket                                                   */
/* ------------------------------------------------------------------ */

/** Busta comune a tutti i messaggi, in entrambe le direzioni. */
export interface Envelope<T extends string, P> {
  v: typeof PROTOCOL_VERSION;
  type: T;
  /** Numero progressivo per connessione, assegnato dal server (solo server→client). */
  seq?: number;
  /** Id del messaggio client, rimandato dal server in `ack`/`error` per correlare la risposta. */
  id?: string;
  ts: EpochMs;
  payload: P;
}

/* ---------- Server → Client ---------- */

export type WelcomeMsg = Envelope<
  'welcome',
  {
    sessionId: string;
    serverTime: EpochMs; // per calcolare lo scarto dell'orologio del kiosk
    heartbeatSec: number; // ogni quanto il client deve mandare `ping`
    siteId: string;
    screen: ScreenId;
    domains: Domain[]; // domini effettivamente concessi
  }
>;

/** Stato completo dei domini concessi. Inviato dopo `welcome` e su richiesta `resync`. */
export type SnapshotMsg = Envelope<'snapshot', { state: Partial<DisplayState> }>;

/** Sostituzione integrale di una slice (le slice sono piccole: niente JSON-Patch). */
export type UpdateMsg = {
  [D in Domain]: Envelope<'update', { domain: D; data: DisplayState[D] }>;
}[Domain];

/** Aggiunta di punti a una serie, senza reinviare tutta la slice. */
export type SeriesAppendMsg = Envelope<
  'series.append',
  {
    domain: 'energy';
    key: string;
    points: Point[];
    /** Valori correnti aggiornati insieme al punto (opzionali). */
    patch?: Partial<
      Pick<
        EnergySlice,
        'powerKw' | 'todayKwh' | 'costTodayEur' | 'peakKw' | 'peakAt' | 'measuredAt'
      >
    >;
  }
>;

/** Eventi puntuali, utili per animazioni e per il registro "Eventi di oggi". */
export type EventKind =
  | 'coffee.brewed'
  | 'room.occupied'
  | 'room.freed'
  | 'feedback.received'
  | 'threshold.crossed'
  | 'device.offline'
  | 'device.online'
  | 'leak.detected';

export type EventMsg = Envelope<
  'event',
  {
    kind: EventKind;
    at: EpochMs;
    roomId?: string;
    deviceId?: string;
    data?: Record<string, number | string | boolean | null>;
  }
>;

/** Comandi operativi inviati dal portale al kiosk. */
export type CommandMsg = Envelope<
  'command',
  {
    action: 'reload' | 'resync' | 'identify' | 'set_brightness';
    args?: Record<string, unknown>;
  }
>;

export type AckMsg = Envelope<'ack', { ok: true }>;
export type PongMsg = Envelope<'pong', { serverTime: EpochMs }>;

export type ErrorCode =
  | 'auth_failed'
  | 'ticket_expired'
  | 'forbidden'
  | 'bad_request'
  | 'unsupported_version'
  | 'rate_limited'
  | 'internal';
export type ErrorMsg = Envelope<'error', { code: ErrorCode; message: string; fatal: boolean }>;

export type ServerMessage =
  | WelcomeMsg
  | SnapshotMsg
  | UpdateMsg
  | SeriesAppendMsg
  | EventMsg
  | CommandMsg
  | AckMsg
  | PongMsg
  | ErrorMsg;

/* ---------- Client → Server ---------- */

/** Primo messaggio obbligatorio: autentica il kiosk con un ticket monouso ottenuto via REST. */
export type HelloMsg = Envelope<
  'hello',
  {
    protocol: typeof PROTOCOL_VERSION;
    ticket: string;
    screen: ScreenId;
    domains: Domain[];
    clientVersion: string;
    userAgent?: string;
  }
>;

export type PingMsg = Envelope<'ping', { clientTime: EpochMs }>;
export type ResyncMsg = Envelope<
  'resync',
  { reason: 'seq_gap' | 'stale' | 'manual' | 'visibility' }
>;

/** Tablet: invio risposte del questionario (idempotente tramite submissionId). */
export type FeedbackSubmitMsg = Envelope<
  'feedback.submit',
  {
    submissionId: string; // uuid generato dal tablet
    flow: 'team' | 'guest';
    answers: { questionId: string; optionId: string }[];
    completedAt: EpochMs;
  }
>;

/**
 * Tablet, percorso ospite: "Contattatemi". Porta i recapiti lasciati dall'ospite, solo se ha dato il consenso.
 * Tutti i campi di `contact` sono facoltativi ma serve almeno `email` o `phone`; `consent` è sempre `true`
 * (senza consenso il tablet non invia nulla). Dati personali: il server li conserva solo per ricontattare l'ospite.
 */
export interface DemoContact {
  name?: string;
  company?: string;
  email?: string;
  phone?: string;
}
export type DemoRequestMsg = Envelope<
  'demo.request',
  { submissionId: string; contact?: DemoContact; consent?: true; interest?: string }
>;

export type ClientMessage = HelloMsg | PingMsg | ResyncMsg | FeedbackSubmitMsg | DemoRequestMsg;

/* ------------------------------------------------------------------ */
/* REST                                                                 */
/* ------------------------------------------------------------------ */

/** POST /api/v1/display/ticket  — body: { deviceKey, screen } (il server li vuole entrambi) */
export interface TicketResponse {
  ticket: string; // monouso, valido 60 s
  expiresAt: EpochMs;
  siteId: string;
  screen: ScreenId;
  wsUrl: string; // assoluto ("wss://tiw.example.it/ws/display/") o relativo ("/ws/display/")
}

/** GET /api/v1/display/snapshot?domains=…  (Authorization: Device <deviceKey>) — fallback se il WS è bloccato */
export interface SnapshotResponse {
  serverTime: EpochMs;
  state: Partial<DisplayState>;
}
